import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { formatArea, formatTime, longDate } from '../../shared/taxonomy';
import { eventUrl, hasEndTime, type Opportunity, type RsvpType } from '../../shared/opportunity';
import { useIsMobile } from '../../shared/useIsMobile';
import { DateField, TimeField } from '../schedule/DateTimeFields';
import { fieldSx } from '../schedule/formStyles';
import PlaceField from '../schedule/PlaceField';
import { areaForCoords } from '../schedule/travel';
import {
  describeChanges,
  diffFields,
  fieldsOf,
  formatOf,
  inPersonOf,
  onlineOf,
  RSVP_OPTIONS,
  type EditScope,
  type EventFields,
} from './eventEdits';

/**
 * "Edit this event": every field of a listing, corrected by someone reading it.
 *
 * Time, location and link get the most care, because they're what sends people
 * to the wrong place at the wrong time. Times are picked from a list, the end
 * can't come before the start, and moving the start carries the end with it.
 * A location can be pinned from address suggestions, and says whether it is.
 * The link is completed as it's typed ("lu.ma/x" becomes "https://lu.ma/x"),
 * names the site it opens, and can be tested before saving.
 */

const DAY_END = 24 * 60 - 1;
const FALLBACK_START = 9 * 60;

const Head = styled('div')({
  position: 'relative',
  padding: '24px 60px 14px 26px',
  borderBottom: '1px solid rgba(243,238,231,0.14)',
});

const Body = styled('div')({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '18px 26px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
});

const Section = styled('section')({ display: 'flex', flexDirection: 'column', gap: 10 });

const SectionLabel = styled('h3')({
  margin: 0,
  fontFamily: FONTS.body,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.13em',
  textTransform: 'uppercase',
  color: 'rgba(243,238,231,0.55)',
});

const Note = styled('p')({
  margin: 0,
  fontFamily: FONTS.body,
  fontSize: 12,
  lineHeight: 1.5,
  color: 'rgba(243,238,231,0.65)',
});

const Foot = styled('div')({
  padding: '14px 26px 18px',
  borderTop: '1px solid rgba(243,238,231,0.14)',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
});

const TestLink = styled('a')({
  flexShrink: 0,
  marginLeft: 8,
  fontFamily: FONTS.body,
  fontSize: 12,
  fontWeight: 700,
  color: BRAND.parchment,
  whiteSpace: 'nowrap',
  textUnderlineOffset: 3,
  '&:hover': { opacity: 0.75 },
});

const checkSx = { color: 'rgba(243,238,231,0.55)', '&.Mui-checked': { color: BRAND.parchment } } as const;
const labelSx = {
  mr: 3,
  '& .MuiFormControlLabel-label': { fontFamily: FONTS.body, fontSize: 14, color: BRAND.parchment },
} as const;
const selectSx = { ...fieldSx, flex: '1 1 180px', '& .MuiSelect-icon': { color: 'rgba(243,238,231,0.6)' } } as const;
const itemSx = {
  fontFamily: FONTS.body,
  '&:hover': { background: 'rgba(243,238,231,0.08)' },
  '&.Mui-selected, &.Mui-selected:hover': { background: 'rgba(243,238,231,0.15)' },
} as const;
const menu = {
  slotProps: {
    paper: {
      sx: {
        background: BRAND.mochaDeep,
        backgroundImage: 'none',
        color: BRAND.parchment,
        maxHeight: 340,
        border: '1px solid rgba(243,238,231,0.18)',
      },
    },
  },
};

const buttonBase = {
  flex: '1 1 200px',
  fontFamily: FONTS.body,
  fontWeight: 700,
  textTransform: 'none',
  borderRadius: 999,
  py: 1.1,
} as const;

/** "lu.ma/x" becomes "https://lu.ma/x". Null when it still isn't a web address. */
function normalizeUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed.replace(/^\/+/, '')}`;
  try {
    const url = new URL(withScheme);
    return url.hostname.includes('.') && !/\s/.test(trimmed) ? withScheme : null;
  } catch {
    return null;
  }
}

const hostOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const titleCase = (word: string) => word.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

function lengthLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return [h ? `${h} hr` : '', m ? `${m} min` : ''].filter(Boolean).join(' ');
}

interface Props {
  event: Opportunity | null;
  /** Every event-type word, for the Format drop-down. */
  formats: string[];
  onClose: () => void;
  onSave: (fields: EventFields, scope: EditScope) => void;
}

export default function EventEditDialog({ event, formats, onClose, onSave }: Props) {
  const mobile = useIsMobile();

  return (
    <Dialog
      open={Boolean(event)}
      onClose={onClose}
      fullScreen={mobile}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            background: BRAND.mochaDeep,
            backgroundImage: 'none',
            color: BRAND.parchment,
            borderRadius: mobile ? 0 : 3,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        },
      }}
    >
      {/* Keyed, so each event opens a fresh form rather than the last one's leftovers. */}
      {event && <EditForm key={event.id} event={event} formats={formats} onClose={onClose} onSave={onSave} />}
    </Dialog>
  );
}

function EditForm({ event, formats, onClose, onSave }: Omit<Props, 'event'> & { event: Opportunity }) {
  const original = useMemo(() => fieldsOf(event), [event]);
  const [form, setForm] = useState<EventFields>(original);
  const [urlText, setUrlText] = useState(eventUrl(event));
  const [urlTouched, setUrlTouched] = useState(false);

  const url = normalizeUrl(urlText);
  const next: EventFields = { ...form, registrationUrl: url ?? form.registrationUrl };
  const changes = describeChanges(original, next);
  const changed = Object.keys(diffFields(original, next)).length > 0;

  const start = form.startMinutes ?? FALLBACK_START;
  const end = form.endMinutes ?? Math.min(start + 60, DAY_END);
  const coords: [number, number] | null =
    form.latitude != null && form.longitude != null ? [form.latitude, form.longitude] : null;
  const area = coords ? areaForCoords(coords) : null;
  const formatOptions = form.eventType && !formats.includes(form.eventType) ? [...formats, form.eventType] : formats;

  const urlProblem = url
    ? null
    : urlText.trim()
      ? "That isn't a full web address yet. Try something like lu.ma/your-event."
      : 'The event needs a link.';
  const problem = !form.name.trim()
    ? 'The event needs a name.'
    : !form.isAllDay && end <= start
      ? 'The end time has to be after the start time.'
      : urlProblem;
  const ready = changed && !problem;

  const setStart = (minutes: number) =>
    setForm((f) => {
      const from = f.startMinutes ?? FALLBACK_START;
      const length = f.endMinutes != null && f.endMinutes > from ? f.endMinutes - from : 60;
      return { ...f, startMinutes: minutes, endMinutes: Math.min(minutes + length, DAY_END) };
    });

  const setEnd = (minutes: number) =>
    setForm((f) => ({ ...f, endMinutes: Math.max(minutes, (f.startMinutes ?? FALLBACK_START) + 5) }));

  const setAllDay = (on: boolean) =>
    setForm((f) =>
      on
        ? { ...f, isAllDay: true }
        : {
            ...f,
            isAllDay: false,
            startMinutes: f.startMinutes ?? FALLBACK_START,
            endMinutes: f.endMinutes ?? (f.startMinutes ?? FALLBACK_START) + 60,
          },
    );

  const { weekday, full } = longDate(form.dayKey);

  return (
    <>
      <Head>
        <Typography component="h2" sx={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 21 }}>
          Edit this event
        </Typography>
        <Typography sx={{ fontSize: 13, opacity: 0.7, mt: 0.4, lineHeight: 1.45 }}>
          Fix anything that's wrong. Keep the changes for yourself, or send them to us to update the
          listing for everyone.
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{ position: 'absolute', top: 14, right: 14, color: BRAND.parchment }}
        >
          ✕
        </IconButton>
      </Head>

      <Body>
        <TextField
          label="Event name"
          fullWidth
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          error={!form.name.trim()}
          slotProps={{ htmlInput: { maxLength: 512 } }}
          sx={fieldSx}
        />

        <Section>
          <SectionLabel>When</SectionLabel>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <DateField value={form.dayKey} onChange={(dayKey) => setForm((f) => ({ ...f, dayKey }))} />
            {!form.isAllDay && (
              <Box sx={{ display: 'flex', gap: 1, flex: '1 1 220px', minWidth: 200, alignItems: 'flex-start' }}>
                <TimeField label="Starts" value={start} onChange={setStart} />
                <TimeField label="Ends" value={end} onChange={setEnd} from={start} />
              </Box>
            )}
          </Box>
          <FormControlLabel
            control={<Checkbox checked={form.isAllDay} onChange={(e) => setAllDay(e.target.checked)} sx={checkSx} />}
            label="No set time (all day, or not published)"
            sx={labelSx}
          />
          <Note>
            {weekday}, {full}
            {form.isAllDay ? ', no set time.' : `, ${formatTime(start)} to ${formatTime(end)} (${lengthLabel(end - start)}).`}
            {!form.isAllDay &&
              !hasEndTime(event) &&
              form.endMinutes === original.endMinutes &&
              " The listing didn't publish an end time, so we assumed one hour. Change it if you know the real one."}
          </Note>
        </Section>

        <Section>
          <SectionLabel>Where</SectionLabel>
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={inPersonOf(form.format)}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, format: formatOf(e.target.checked, onlineOf(f.format)) }))
                  }
                  sx={checkSx}
                />
              }
              label="In person"
              sx={labelSx}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={onlineOf(form.format)}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, format: formatOf(inPersonOf(f.format), e.target.checked) }))
                  }
                  sx={checkSx}
                />
              }
              label="Online"
              sx={labelSx}
            />
          </Box>
          <PlaceField
            label={!inPersonOf(form.format) && onlineOf(form.format) ? 'Location (optional for online events)' : 'Location'}
            value={form.address}
            coords={coords}
            onChange={(address, picked) =>
              setForm((f) => ({ ...f, address, latitude: picked?.[0] ?? null, longitude: picked?.[1] ?? null }))
            }
            hint={(located) =>
              located
                ? `Pinned on the map${area ? `, in ${formatArea(area)}` : ''}. Travel times start and end here.`
                : 'Pick an address from the suggestions to pin it on the map. A typed location still shows, without a map or travel times.'
            }
          />
        </Section>

        <Section>
          <SectionLabel>Details</SectionLabel>
          <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap' }}>
            <TextField
              select
              label="Format"
              value={form.eventType ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, eventType: e.target.value || null }))}
              sx={selectSx}
              slotProps={{ select: { MenuProps: menu, displayEmpty: true }, inputLabel: { shrink: true } }}
            >
              <MenuItem value="" sx={itemSx}>
                Not listed
              </MenuItem>
              {formatOptions.map((word) => (
                <MenuItem key={word} value={word} sx={itemSx}>
                  {titleCase(word)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="RSVP"
              value={form.rsvpType}
              onChange={(e) => setForm((f) => ({ ...f, rsvpType: e.target.value as RsvpType }))}
              sx={selectSx}
              slotProps={{ select: { MenuProps: menu, renderValue: (value) => RSVP_OPTIONS.find((o) => o.value === value)?.label ?? String(value) } }}
            >
              {RSVP_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ ...itemSx, display: 'block', py: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{option.label}</Typography>
                  <Typography sx={{ fontSize: 11.5, opacity: 0.65 }}>{option.hint}</Typography>
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <TextField
            label="Description"
            multiline
            minRows={4}
            maxRows={14}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            slotProps={{ htmlInput: { maxLength: 10000 } }}
            sx={fieldSx}
          />
        </Section>

        <Section>
          <SectionLabel>Link</SectionLabel>
          <TextField
            label="Event page"
            type="url"
            fullWidth
            value={urlText}
            onChange={(e) => setUrlText(e.target.value)}
            onBlur={() => {
              setUrlTouched(true);
              if (url) setUrlText(url);
            }}
            error={urlTouched && Boolean(urlProblem)}
            helperText={
              url ? `Opens ${hostOf(url)}` : urlTouched ? urlProblem : 'The page where people find out more or register.'
            }
            sx={fieldSx}
            slotProps={{
              htmlInput: {
                inputMode: 'url',
                autoCapitalize: 'off',
                autoCorrect: 'off',
                spellCheck: false,
                maxLength: 2048,
              },
              input: {
                endAdornment: url ? (
                  <TestLink href={url} target="_blank" rel="noopener noreferrer">
                    Test link ↗
                  </TestLink>
                ) : undefined,
              },
            }}
          />
        </Section>
      </Body>

      <Foot>
        <Note role="status">
          {changed && problem
            ? problem
            : changed
              ? changes.length > 0
                ? `${changes.length} ${changes.length === 1 ? 'change' : 'changes'} ready.`
                : 'Ready to save.'
              : 'Change something above to save it.'}
        </Note>
        <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap' }}>
          <Button
            disabled={!ready}
            onClick={() => onSave(next, 'me')}
            sx={{
              ...buttonBase,
              color: BRAND.parchment,
              border: '1px solid rgba(243,238,231,0.45)',
              '&:hover': { borderColor: BRAND.parchment, background: 'rgba(243,238,231,0.07)' },
              '&.Mui-disabled': { color: 'rgba(243,238,231,0.35)', borderColor: 'rgba(243,238,231,0.18)' },
            }}
          >
            Save changes for me
          </Button>
          <Button
            disabled={!ready}
            onClick={() => onSave(next, 'everyone')}
            sx={{
              ...buttonBase,
              background: BRAND.parchment,
              color: BRAND.mocha,
              '&:hover': { background: '#fff' },
              '&.Mui-disabled': { background: 'rgba(243,238,231,0.22)', color: 'rgba(54,47,42,0.55)' },
            }}
          >
            Submit changes for everyone
          </Button>
        </Box>
      </Foot>
    </>
  );
}
